import React, { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TooltipPortal
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface ActivityStatusProps {
  status: string;
  direction?: 'sent' | 'received';
  declineReason?: string;
  declinerImage?: string;
  declinerName?: string;
}

const ActivityStatus: React.FC<ActivityStatusProps> = ({
  status,
  direction,
  declineReason,
  declinerImage,
  declinerName
}) => {
  const isTouch = useMediaQuery('(max-width: 767px)');
  const [open, setOpen] = useState(false);

  const getDisplayText = () => {
    if (status === 'new_request')
      return direction === 'received' ? 'New Request' : 'Request Sent';
    if (status === 'update_requested') return 'Update Requested';
    if (status === 'declined') return 'Declined';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const statusColors = () =>
    status === 'new_request' && direction === 'received'
      ? 'bg-amber-100 dark:bg-yellow-400/20 text-amber-700 dark:text-yellow-400'
      : status === 'new_request' && direction === 'sent'
      ? 'bg-blue-100 dark:bg-blue-400/20 text-blue-700 dark:text-blue-400'
      : status === 'confirmed'
      ? 'bg-green-100 dark:bg-green-400/20 text-green-700 dark:text-green-400'
      : status === 'played'
      ? 'bg-gold-100 dark:bg-gold-400/20 text-gold-700 dark:text-gold-400'
      : status === 'declined'
      ? 'bg-rose-100 dark:bg-rose-400/20 text-rose-700 dark:text-rose-400'
      : status === 'update_requested'
      ? 'bg-amber-100 dark:bg-yellow-400/20 text-amber-700 dark:text-yellow-400'
      : status === 'disputed'
      ? 'bg-rose-100 dark:bg-rose-400/20 text-rose-700 dark:text-rose-400'
      : status === 'missed'
      ? 'bg-gray-100 dark:bg-gray-400/20 text-gray-700 dark:text-gray-400'
      : 'bg-gray-100 dark:bg-dark-400/20 text-gray-700 dark:text-dark-800';

  const Body = () => (
    <div className="flex items-start gap-4">
      {declinerImage && (
        <img
          src={declinerImage}
          alt={declinerName ?? 'Decliner'}
          className="w-14 h-14 rounded-full object-cover border-2 border-rose-300 dark:border-rose-500/40 flex-shrink-0 shadow-md"
        />
      )}
      <p className="text-sm italic p-2 rounded-lg
                    bg-white/60 dark:bg-rose-900/20
                    text-rose-900 dark:text-white/90
                    border border-rose-200 dark:border-rose-700/40 flex-1">
        “{declineReason}”
      </p>
    </div>
  );

  // ─────────── Desktop ───────────
  if (!isTouch) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'flex items-center px-3 py-2 rounded-lg text-sm',
                statusColors(),
                status === 'declined' && declineReason
                  ? 'cursor-help hover:bg-rose-200/60 dark:hover:bg-rose-400/30'
                  : ''
              )}
            >
              {getDisplayText()}
              {status === 'declined' && declineReason && (
                <MessageSquare className="h-3.5 w-3.5 ml-1.5 flex-shrink-0 animate-pulse" />
              )}
            </div>
          </TooltipTrigger>

          {status === 'declined' && declineReason && (
            <TooltipPortal>
              <TooltipContent
                sideOffset={10}
                className="max-w-xs p-4 rounded-xl shadow-xl ring-1 ring-rose-500/10
                           bg-rose-50 dark:bg-[#1A1A1D]
                           border border-rose-200 dark:border-rose-800"
              >
                <p className="text-sm font-semibold mb-2 text-rose-700 dark:text-rose-400">
                  Declined by {declinerName ?? 'unknown'}
                </p>
                <Body />
              </TooltipContent>
            </TooltipPortal>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  // ─────────── Mobile ───────────
  return (
    <>
      <div
        className={cn(
          'flex items-center px-3 py-2 rounded-lg text-sm cursor-pointer',
          statusColors()
        )}
        onClick={() => {
          if (status === 'declined' && declineReason) setOpen(true);
        }}
      >
        {getDisplayText()}
        {status === 'declined' && (
          <MessageSquare className="h-3.5 w-3.5 ml-1.5 flex-shrink-0 animate-pulse" />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="sm:max-w-sm bg-rose-50 dark:bg-[#1A1A1D]
                     border border-rose-200 dark:border-rose-800
                     rounded-xl shadow-xl ring-1 ring-rose-500/10 px-4 pt-4 pb-5"
        >
          <DialogHeader className="mb-3">
            <DialogTitle className="text-rose-700 dark:text-rose-400 text-base font-semibold">
              Declined by {declinerName ?? 'unknown'}
            </DialogTitle>
          </DialogHeader>

          <Body />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActivityStatus;
